import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
} from '@mui/material';
import { useCreateUser } from '../hooks/useUserMutations';
import { useUser } from '@/store/useAuthStore';
import type { CreateUserRequest } from '../types/user.types';
import type { UserRole } from '@/types/auth';

const ROLE_PRIORITY: Record<UserRole, number> = {
  admin: 5,
  'user-manager': 4,
  lab_manager: 3,
  supervisor: 3,
  researcher: 2,
  operator: 1,
};

const ALL_ROLES: { value: UserRole; label: string }[] = [
  { value: 'operator', label: 'Operator' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'researcher', label: 'Researcher' },
  { value: 'lab_manager', label: 'Lab Manager' },
  { value: 'user-manager', label: 'User Manager' },
  { value: 'admin', label: 'Admin' },
];

const createUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  fullName: z.string().min(1, 'Name is required').max(100),
  role: z.enum(['operator', 'supervisor', 'researcher', 'lab_manager', 'user-manager', 'admin']),
  temporaryPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain uppercase letter')
    .regex(/[a-z]/, 'Password must contain lowercase letter')
    .regex(/[0-9]/, 'Password must contain a number'),
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

interface UserCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function UserCreateDialog({ open, onClose, onSuccess }: UserCreateDialogProps) {
  const createUser = useCreateUser();
  const currentUser = useUser();
  const currentUserMaxPriority = Math.max(
    0,
    ...(currentUser?.roles ?? []).map((r) => ROLE_PRIORITY[r] ?? 0)
  );
  const isAdmin = currentUserMaxPriority >= ROLE_PRIORITY['admin'];
  const assignableRoles = ALL_ROLES.filter((r) =>
    isAdmin
      ? true // admins can assign any role
      : ROLE_PRIORITY[r.value] < currentUserMaxPriority // non-admins: strictly lower roles only
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      fullName: '',
      role: 'operator',
      temporaryPassword: '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateUserFormData) => {
    try {
      const request: CreateUserRequest = {
        email: data.email,
        fullName: data.fullName,
        role: data.role,
        temporaryPassword: data.temporaryPassword,
        // labIds intentionally omitted - auto-assigns to default lab
      };
      await createUser.mutateAsync(request);
      handleClose();
      onSuccess();
    } catch (error) {
      // Error handled by mutation
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            {createUser.error && (
              <Alert severity="error">
                {createUser.error instanceof Error
                  ? createUser.error.message
                  : 'Failed to create user'}
              </Alert>
            )}
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
            <Controller
              name="fullName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Full Name"
                  fullWidth
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                />
              )}
            />
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select {...field} label="Role">
                    {assignableRoles.map((r) => (
                      <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="temporaryPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Temporary Password"
                  type="password"
                  fullWidth
                  error={!!errors.temporaryPassword}
                  helperText={
                    errors.temporaryPassword?.message ||
                    'Min 8 chars: uppercase, lowercase, number'
                  }
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || createUser.isPending}
          >
            Create User
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
